import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { trigger, style, animate, transition } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { SessionService, SessionState } from '../../services/session.service';
import { IdleService } from '../../services/idle.Service';
import { AuthService } from '../../services/auth.service';
import { Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-session-expiration-warning',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatFormFieldModule, MatInputModule],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate('300ms ease', style({ opacity: 1 }))]),
      transition(':leave', [animate('300ms ease', style({ opacity: 0 }))]),
    ]),
  ],
  template: `
    <div class="session-warning-overlay" *ngIf="isVisible" [@fadeInOut]>
      <div class="session-warning-modal">
        <div class="warning-icon" [class.inactivity]="isInactivityWarning">
          <mat-icon>{{ isInactivityWarning ? 'hourglass_empty' : 'timer_off' }}</mat-icon>
        </div>

        <h2>{{ isInactivityWarning ? 'Inatividade Detectada' : 'Sessão a Expirar' }}</h2>

        <p>{{ warningMessage }}</p>

        <div class="countdown">
          <span class="time">{{ formattedTime }}</span>
        </div>

        <p class="warning-text">
          {{
            isInactivityWarning
              ? 'Mova o rato ou prima uma tecla para continuar.'
              : 'Se não houver interação, a sessão será terminada automaticamente.'
          }}
        </p>

        <div class="actions">
          <button mat-stroked-button (click)="logout()">
            <mat-icon>logout</mat-icon>
            Terminar Agora
          </button>
          <button mat-raised-button color="primary" class="btn-continue" (click)="continueSession()">
            <mat-icon class="icon-white">refresh</mat-icon>
            <span style="color: white !important">Continuar Sessão</span>
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .session-warning-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(5px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        animation: fadeIn 0.3s ease;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      .session-warning-modal {
        background: white;
        border-radius: 16px;
        padding: 40px;
        text-align: center;
        max-width: 500px;
        width: 95%;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        animation: slideUp 0.3s ease;
      }

      @keyframes slideUp {
        from {
          transform: translateY(30px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      .warning-icon {
        width: 80px;
        height: 80px;
        background: linear-gradient(135deg, #2bfdb5 0%, #1c1463 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 20px;
        animation: pulse 1.5s infinite;
      }

      .warning-icon.inactivity {
        background: linear-gradient(135deg, #2bfdb5 0%, #1c1463 100%);
      }

      @keyframes pulse {
        0%,
        100% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.05);
        }
      }

      .warning-icon mat-icon {
        font-size: 40px;
        width: 40px;
        height: 40px;
        color: white;
      }

      h2 {
        font-size: 24px;
        font-weight: 600;
        color: #333;
        margin: 0 0 10px;
      }

      p {
        color: #666;
        margin: 0 0 15px;
        font-size: 14px;
      }

      .countdown {
        background: linear-gradient(135deg, #1c1463 0%, #0d0933 100%);
        color: white !important;
        padding: 20px 40px;
        border-radius: 12px;
        margin: 20px 0;
      }

      .inactivity + h2 + p + .countdown {
        background: linear-gradient(135deg, #1c1463 0%, #0d0933 100%);
      }

      .time {
        font-size: 48px;
        font-weight: 700;
        font-family: 'Roboto Mono', monospace;
        letter-spacing: 4px;
        color: #ffffff !important;
      }

      .warning-text {
        font-size: 12px;
        color: #999;
        margin-bottom: 25px;
      }

      .actions {
        display: flex;
        gap: 15px;
        justify-content: center;
        flex-wrap: wrap;
      }

      .actions button {
        padding: 10px 16px;
        border-radius: 8px;
        font-size: 13px;
        min-width: 140px;
      }

      .actions button mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
        margin-right: 6px;
      }

      .btn-continue {
        color: #ffffff !important;
      }

      .icon-white {
        color: #ffffff !important;
      }

      @media (max-width: 480px) {
        .session-warning-modal {
          padding: 30px 20px;
        }

        .time {
          font-size: 36px;
        }

        .actions {
          flex-direction: column;
        }

        .actions button {
          width: 100%;
        }
      }
    `,
  ],
})
export class SessionExpirationWarningComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isVisible = false;
  isInactivityWarning = false;
  isRenewing = false; // Flag to indicate if session renewal is in progress
  formattedTime = '00:00';
  warningMessage = 'A sua sessão irá terminar em:';

  constructor(
    private sessionService: SessionService,
    private idleService: IdleService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Subscrever ao estado da sessão (token expiring)
    this.sessionService.sessionState$.pipe(takeUntil(this.destroy$)).subscribe((state: SessionState) => {
      // Se estivermos em modo de inatividade ou renovação, ignoramos updates de sessão
      if (this.isInactivityWarning || this.isRenewing) {
        return;
      }

      if (state.showWarning) {
        this.isVisible = true;
        this.warningMessage = 'A sua sessão irá terminar em:';
        this.formattedTime = this.sessionService.getFormattedTimeRemaining();
        this.cdr.detectChanges(); // Força atualização da UI
      } else {
        // Se já não há aviso (ex: renovado noutra tab), esconder se estiver visível e não for inatividade
        if (this.isVisible) {
          this.isVisible = false;
          this.cdr.detectChanges();
        }
      }
    });

    // Verificar inatividade periodicamente
    interval(500)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.idleService.showInactivityWarning) {
          this.isVisible = true;
          this.isInactivityWarning = true;
          this.warningMessage = 'Sem atividade. A sessão será terminada em:';
          this.formattedTime = this.idleService.getFormattedTime();
          this.cdr.detectChanges();
        } else if (this.isInactivityWarning) {
          // Inatividade foi cancelada
          this.isVisible = false;
          this.isInactivityWarning = false;
          this.cdr.detectChanges();
        }
      });

    // Iniciar monitorização de sessão
    this.sessionService.startMonitoring();
  }

  continueSession(): void {
    if (this.isInactivityWarning) {
      this.idleService.dismissWarning();
      this.isVisible = false;
      this.isInactivityWarning = false;
      this.cdr.detectChanges();
    } else {
      this.isRenewing = true;
      this.isVisible = false; // Hide immediate
      this.cdr.detectChanges();

      this.sessionService.renewSession().then(() => {
        // Wait a small delay to ensure session check interval picks up the new token
        setTimeout(() => {
          this.isRenewing = false;
          this.cdr.detectChanges();
        }, 1500);
      });
    }
  }

  logout(): void {
    this.isVisible = false;
    this.authService.logout();
  }

  ngOnDestroy(): void {
    if (!this.destroy$.closed) {
      this.destroy$.next();
      this.destroy$.complete();
    }
  }
}
